import { Button, enums as e, Heading, Pill, utils as u } from 'edrolo-ui';
import React, { ReactElement } from 'react';
import { toast as sonnerToast } from 'sonner';
import { Icon, IconProps } from '../Icon/Icon';
import s from './styles.scss';

interface ButtonConfig {
  label: string;
  onClick: () => void;
  color?: e.ColorTheme;
  variant?: e.Variant;
  dismissOnClick?: boolean; // defaults to true
}

type ToastActions =
  | ButtonConfig
  | ButtonConfig[]
  | ReactElement
  | null
  | undefined;

interface ToastProps {
  id: string | number;
  title: string;
  icon?: IconProps;
  description?: string;
  actions?: ToastActions;
}

export function toast({
  icon,
  title,
  description,
  actions,
}: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom((id: ToastProps['id']) => (
    <Toast
      icon={icon}
      id={id}
      title={title}
      description={description}
      actions={actions}
    />
  ));
}

const isReactElement = (value: unknown): value is ReactElement => {
  return (
    value !== null &&
    typeof value === 'object' &&
    '$typeof' in (value as object)
  );
};

interface ToastButtonsProps {
  actions?: ToastActions;
  toastId: string | number;
  className?: string;
}

function ToastButtons({ actions, toastId, className }: ToastButtonsProps) {
  if (isReactElement(actions)) {
    // For functional components, provide dismiss as prop
    if (typeof actions.type === 'function') {
      const dismiss = () => sonnerToast.dismiss(toastId);
      return (
        <div className={className}>
          {React.cloneElement(actions, { dismiss })}
        </div>
      );
    }
    return <div className={className}>{actions}</div>;
  }

  if (!actions) {
    return (
      <div className={className}>
        <Button
          variant={e.Variant.FADE}
          size={e.Size.SMALL}
          onClick={() => sonnerToast.dismiss(toastId)}
        >
          Dismiss
        </Button>
      </div>
    );
  }

  if (Array.isArray(actions)) {
    return (
      <div className={className}>
        {actions.map((button, index) => (
          <Button
            key={index}
            variant={button.variant || e.Variant.FADE}
            size={e.Size.SMALL}
            color={button.color}
            onClick={() => {
              button.onClick();
              if (button.dismissOnClick !== false) {
                sonnerToast.dismiss(toastId);
              }
            }}
          >
            {button.label}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      <Button
        variant={actions.variant || e.Variant.FADE}
        size={e.Size.SMALL}
        color={actions.color}
        onClick={() => {
          actions.onClick();
          if (actions.dismissOnClick !== false) {
            sonnerToast.dismiss(toastId);
          }
        }}
      >
        {actions.label}
      </Button>
    </div>
  );
}

export function Toast({ title, description, id, icon, actions }: ToastProps) {
  return (
    <div className={s.toast}>
      {icon && (
        <div>
          <Pill
            variant={e.PillVariant.CIRCLE}
            size={icon.size}
            color={icon.color}
          >
            <Icon name={icon.name} size={icon.size} />
          </Pill>
        </div>
      )}
      <div>
        <Heading level={4} className={u.mb2}>
          {title}
        </Heading>
        {description && <p>{description}</p>}
        <ToastButtons
          actions={actions}
          toastId={id}
          className={s.buttonContainer}
        />
      </div>
    </div>
  );
}
